import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoShareSocialOutline, IoLogoWhatsapp, IoCopyOutline, IoClose } from "react-icons/io5";
import { PiTelegramLogoFill } from "react-icons/pi";
import Toast from "./Toast";
import { supabase } from "../supabase";
import { copyText } from "../utils/copyText";
import { useFocusTrap } from "../utils/focusTrap";

export default function ShareButton({
  designId,
  url,
  text,
  label,
  icon: LabelIcon = IoShareSocialOutline,
  // Draws the tap target itself: renderTrigger(openShare) — for menus that have
  // their own row styling.
  renderTrigger
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState("");
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, menuOpen);

  // Every other overlay in the app (FollowListModal, ReportModal,
  // ImageCropModal, ImageLightbox, NotificationBell, OnboardingModal,
  // QRCodeModal) locks background scroll and closes on Escape — this one
  // was missing both.
  useEffect(() => {
    if (!menuOpen) return;

    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const shareUrl = url || `${window.location.origin}/d/${designId}`;
  // The caption isn't repeated here: the link's preview (WhatsApp, Telegram…)
  // already shows the design's photo and caption, so it only appeared twice.
  const shareText = text || "Check out this design on GatherYou";

  // Only design shares are logged for admin analytics (design_engagement_log.design_id
  // is required) — profile shares (no designId) have nothing to attach the event to.
  function logShare() {
    if (!designId) return;
    supabase.rpc("log_design_share", { target_design_id: designId }).then(({ error }) => {
      if (error) console.log("LOG SHARE ERROR:", error);
    });
  }

  async function handleShare(e) {
    e.stopPropagation();

    if (navigator.share) {
      try {
        await navigator.share({ title: "GatherYou", text: shareText, url: shareUrl });
        logShare();
        return;
      } catch (err) {
        // AbortError just means the user closed the native share sheet —
        // leave it at that. Any OTHER failure falls through to the custom
        // menu below instead of leaving the tap looking like it did nothing.
        if (err.name === "AbortError") return;
        console.log("SHARE ERROR:", err);
      }
    }

    setMenuOpen(true);
  }

  function shareToWhatsApp() {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    logShare();
    setMenuOpen(false);
  }

  function shareToTelegram() {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    logShare();
    setMenuOpen(false);
  }

  async function copyLink() {
    if (await copyText(shareUrl)) {
      setToast("Link copied");
      logShare();
    } else {
      setToast("Couldn't copy link");
    }
    setMenuOpen(false);
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleShare)
      ) : label ? (
        <div onClick={handleShare} style={menuRowBtn}>
          <LabelIcon size={16} /> {label}
        </div>
      ) : (
        <button onClick={handleShare} style={btn} aria-label="Share">
          <IoShareSocialOutline size={25} />
        </button>
      )}

      {menuOpen &&
        // Portaled — this is called from deep inside card structures (Home,
        // PublicProfile, Trending, ...), so an ancestor with a CSS transform/
        // filter (an image carousel's heart-burst animation, say) could
        // otherwise hijack this backdrop's position:fixed to be relative to
        // THAT ancestor's box instead of the viewport (same class of bug
        // just fixed in Toast.jsx) — inset:0 on the wrong containing block
        // would only cover that ancestor's box, not the full screen.
        createPortal(
          <div style={backdrop} onClick={() => setMenuOpen(false)}>
            <div style={sheet} ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Share" onClick={e => e.stopPropagation()}>
              <button style={menuItem} onClick={shareToWhatsApp}>
                <IoLogoWhatsapp size={18} color="#25D366" /> Share to WhatsApp
              </button>
              <button style={menuItem} onClick={shareToTelegram}>
                <PiTelegramLogoFill size={18} color="#229ED9" /> Share to Telegram
              </button>
              <button style={menuItem} onClick={copyLink}>
                <IoCopyOutline size={18} /> Copy link
              </button>
              <button
                style={{ ...menuItem, borderBottom: "none", color: "var(--text-muted)" }}
                onClick={() => setMenuOpen(false)}
              >
                <IoClose size={18} /> Cancel
              </button>
            </div>
          </div>,
          document.body
        )}

      {toast && <Toast text={toast} onClose={() => setToast("")} />}
    </>
  );
}

const menuRowBtn = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  cursor: "pointer",
  fontSize: 13.5,
  color: "var(--text-heading)",
  borderBottom: "1px solid var(--border-soft)",
};

const btn = {
  display: "flex",
  alignItems: "center",
  height: 40,
  padding: "0 2px",
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  cursor: "pointer",
};

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,14,8,0.55)",
  zIndex: 1100,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
};

const sheet = {
  background: "var(--bg-surface)",
  width: "100%",
  maxWidth: 420,
  borderRadius: "18px 18px 0 0",
  overflow: "hidden",
  paddingBottom: "env(safe-area-inset-bottom)",
};

const menuItem = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "14px 18px",
  border: "none",
  borderBottom: "1px solid var(--border-soft)",
  background: "none",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--text-heading)",
  cursor: "pointer",
  textAlign: "left",
};
